import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Mail, RefreshCw, Trash2, Send, Eye, Filter, ArrowLeft,
  CheckCircle, XCircle, AlertCircle, Clock
} from 'lucide-react';

const TYPE_LABELS = {
  order_confirmation: 'Order Confirmation',
  new_order_admin: 'New Order (Admin)',
  order_cancellation: 'Cancellation',
  customer_cancellation: 'Cust. Cancellation',
  delivery_shipped: 'Shipped',
  delivery_delivered: 'Delivered',
  delivery_confirmation: 'Delivery Photo',
  subscription_reminder: 'Sub. Reminder',
  subscription_autocharge: 'Auto-Charge',
  subscription_payment_failed: 'Payment Failed',
  subscription_paused: 'Sub. Paused',
  subscription_resumed: 'Sub. Resumed',
  subscription_skipped: 'Sub. Skipped',
  subscription_cancelled: 'Sub. Cancelled',
  resend: 'Resent',
  general: 'General',
};

const STATUS_CONFIG = {
  sent: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  failed: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  skipped: { icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
  pending: { icon: Clock, color: 'text-slate-500', bg: 'bg-slate-50' },
};

export default function EmailActivity() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showResend, setShowResend] = useState(false);
  const [resendForm, setResendForm] = useState({ to: '', subject: '', html: '' });
  const [sending, setSending] = useState(false);
  const [purging, setPurging] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const { toast } = useToast();
  const navigate = useNavigate();

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterType ? `?email_type=${filterType}` : '';
      const res = await api.get(`/email-logs${params}`);
      setLogs(res.data);
      setSelectedIds(new Set());
    } catch {
      toast({ title: 'Error', description: 'Failed to load email logs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filterType, toast]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Selection helpers
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === logs.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(logs.map(l => l.id)));
    }
  };

  const isAllSelected = logs.length > 0 && selectedIds.size === logs.length;

  // Purge selected or all
  const handlePurgeSelected = async () => {
    const count = selectedIds.size;
    if (count === 0) return;
    const label = count === logs.length ? 'ALL' : `${count} selected`;
    if (!window.confirm(`Permanently delete ${label} email log(s)? This cannot be undone.`)) return;
    setPurging(true);
    try {
      if (count === logs.length) {
        const res = await api.delete('/email-logs/purge');
        toast({ title: 'Purged', description: `${res.data.deleted_count} email logs deleted` });
        setLogs([]);
      } else {
        // Delete individually
        await Promise.all([...selectedIds].map(id => api.delete(`/email-logs/${id}`)));
        setLogs(prev => prev.filter(l => !selectedIds.has(l.id)));
        toast({ title: 'Deleted', description: `${count} email log(s) deleted` });
      }
      setSelectedIds(new Set());
    } catch {
      toast({ title: 'Error', description: 'Purge failed', variant: 'destructive' });
    } finally {
      setPurging(false);
    }
  };

  const openPreview = (log) => {
    setSelectedLog(log);
    setShowPreview(true);
  };

  const openResend = (log) => {
    setSelectedLog(log);
    setResendForm({
      to: (log.to || []).join(', '),
      subject: log.subject || '',
      html: log.html || '',
    });
    setShowResend(true);
  };

  const handleResend = async () => {
    if (!selectedLog) return;
    setSending(true);
    try {
      const toArr = resendForm.to.split(',').map(s => s.trim()).filter(Boolean);
      await api.post(`/email-logs/${selectedLog.id}/resend`, {
        to: toArr,
        subject: resendForm.subject,
        html: resendForm.html,
      });
      toast({ title: 'Email Resent', description: `Sent to ${toArr.join(', ')}` });
      setShowResend(false);
      loadLogs();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Resend failed', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const uniqueTypes = [...new Set(logs.map(l => l.email_type).filter(Boolean))];

  return (
    <Layout>
      <div className="space-y-4" data-testid="email-activity-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="email-back-btn"
              className="text-slate-500 hover:text-slate-800 -ml-2">
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800" data-testid="email-activity-title">Email Activity</h1>
              <p className="text-sm text-slate-500 mt-0.5">{logs.length} notification emails logged</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={loadLogs} disabled={loading} data-testid="refresh-emails-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="destructive" size="sm" onClick={handlePurgeSelected}
              disabled={purging || selectedIds.size === 0} data-testid="purge-selected-btn">
              <Trash2 className="h-4 w-4 mr-1.5" />
              {purging ? 'Deleting...' : selectedIds.size === logs.length && logs.length > 0
                ? `Purge All (${logs.length})`
                : selectedIds.size > 0
                  ? `Delete Selected (${selectedIds.size})`
                  : 'Select to Delete'}
            </Button>
          </div>
        </div>

        {/* Filters & Selection Bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-slate-400" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="text-sm border rounded-md px-2 py-1.5 bg-white"
              data-testid="email-type-filter"
            >
              <option value="">All Types</option>
              {uniqueTypes.map(t => (
                <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>
              ))}
            </select>
          </div>
          {selectedIds.size > 0 && (
            <span className="text-xs font-medium text-teal-700 bg-teal-50 px-3 py-1 rounded-full">
              {selectedIds.size} of {logs.length} selected
            </span>
          )}
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="email-logs-table">
                <thead>
                  <tr className="border-b bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
                    <th className="px-3 py-3 w-10">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={toggleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                    </th>
                    <th className="px-3 py-3">Status</th>
                    <th className="px-3 py-3">Type</th>
                    <th className="px-3 py-3">To</th>
                    <th className="px-3 py-3">Subject</th>
                    <th className="px-3 py-3">Related</th>
                    <th className="px-3 py-3">Date</th>
                    <th className="px-3 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center text-slate-400">
                        <Mail className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        No email logs found
                      </td>
                    </tr>
                  )}
                  {logs.map((log) => {
                    const st = STATUS_CONFIG[log.status] || STATUS_CONFIG.pending;
                    const Icon = st.icon;
                    const isChecked = selectedIds.has(log.id);
                    return (
                      <tr key={log.id} className={`border-b transition-colors ${isChecked ? 'bg-teal-50/40' : 'hover:bg-slate-50/50'}`}
                        data-testid={`email-row-${log.id}`}>
                        <td className="px-3 py-3">
                          <Checkbox
                            checked={isChecked}
                            onCheckedChange={() => toggleSelect(log.id)}
                            data-testid={`select-checkbox-${log.id}`}
                          />
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${st.bg} ${st.color}`}>
                            <Icon className="h-3 w-3" />
                            {log.status}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                            {TYPE_LABELS[log.email_type] || log.email_type}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-slate-700 max-w-[180px] truncate" title={(log.to||[]).join(', ')}>
                          {(log.to || []).join(', ')}
                        </td>
                        <td className="px-3 py-3 text-slate-700 max-w-[250px] truncate" title={log.subject}>
                          {log.subject}
                        </td>
                        <td className="px-3 py-3">
                          {log.related_id && (
                            <span className="text-xs font-mono text-slate-500">{log.related_id}</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-slate-500 text-xs whitespace-nowrap">
                          {formatDate(log.sent_at)}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openPreview(log)} data-testid={`preview-btn-${log.id}`}
                              className="h-7 w-7 p-0 text-slate-400 hover:text-blue-600">
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => openResend(log)} data-testid={`resend-btn-${log.id}`}
                              className="h-7 w-7 p-0 text-slate-400 hover:text-green-600">
                              <Send className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Preview Dialog */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-teal-600" /> Email Preview
              </DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-slate-500">To:</span> <span className="font-medium">{(selectedLog.to || []).join(', ')}</span></div>
                  <div><span className="text-slate-500">From:</span> <span className="font-medium">{selectedLog.from_email}</span></div>
                  <div className="col-span-2"><span className="text-slate-500">Subject:</span> <span className="font-medium">{selectedLog.subject}</span></div>
                  <div><span className="text-slate-500">Status:</span> <span className="font-medium">{selectedLog.status}</span></div>
                  <div><span className="text-slate-500">Sent:</span> <span className="font-medium">{formatDate(selectedLog.sent_at)}</span></div>
                  {selectedLog.error && (
                    <div className="col-span-2 text-red-600 text-xs bg-red-50 p-2 rounded">Error: {selectedLog.error}</div>
                  )}
                </div>
                <div className="border rounded-lg p-4 bg-white">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedLog.html || '') }} />
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Resend/Edit Dialog */}
        <Dialog open={showResend} onOpenChange={setShowResend}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-green-600" /> Edit & Resend Email
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-slate-700">To (comma-separated)</label>
                <Input value={resendForm.to} onChange={(e) => setResendForm(p => ({ ...p, to: e.target.value }))}
                  placeholder="email@example.com" data-testid="resend-to-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Subject</label>
                <Input value={resendForm.subject} onChange={(e) => setResendForm(p => ({ ...p, subject: e.target.value }))}
                  data-testid="resend-subject-input" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">HTML Content</label>
                <textarea
                  value={resendForm.html}
                  onChange={(e) => setResendForm(p => ({ ...p, html: e.target.value }))}
                  className="w-full border rounded-md p-3 text-sm font-mono min-h-[200px] resize-y"
                  data-testid="resend-html-input"
                />
              </div>
              <div className="border rounded-lg p-3 bg-slate-50">
                <p className="text-xs text-slate-500 mb-2">Preview:</p>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(resendForm.html || '') }} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResend(false)}>Cancel</Button>
              <Button onClick={handleResend} disabled={sending} data-testid="confirm-resend-btn"
                className="bg-teal-600 hover:bg-teal-700 text-white">
                <Send className="h-4 w-4 mr-1.5" />
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
